import React, { useEffect, useRef } from 'react';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import { subscribeManyPresetStickers, PresetStickersResponse } from '@/lib/collections/presetStickers';
import { Layers } from 'lucide-react';

// Danger Room color constants (mirrored from DangerRoomPage)
const DANGER_RED = '#E63946';
const ACCENT_GREEN = '#7CFC00';
const BG_CARD = 'rgba(14, 18, 38, 0.98)';
const BORDER = 'rgba(230, 57, 70, 0.2)';

interface StickerPickerProps {
  open: boolean;
  onClose: () => void;
  onSelect: (stickerUrl: string) => void;
}

export const StickerPicker: React.FC<StickerPickerProps> = ({ open, onClose, onSelect }) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const { data: stickers } = useRealtimeData<PresetStickersResponse[]>(
    subscribeManyPresetStickers,
    open,
  );

  const sortedStickers = [...(stickers ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      ref={containerRef}
      style={{
        position: 'absolute',
        bottom: 'calc(100% + 8px)',
        left: 0,
        zIndex: 60,
        width: 280,
        background: BG_CARD,
        border: `1px solid ${BORDER}`,
        borderRadius: 8,
        boxShadow: `0 -8px 32px rgba(0,0,0,0.7), 0 0 0 1px rgba(230,57,70,0.08)`,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '7px 12px',
          borderBottom: `1px solid ${BORDER}`,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          background: 'rgba(230,57,70,0.06)',
        }}
      >
        <Layers size={12} style={{ color: DANGER_RED }} />
        <span
          style={{
            fontFamily: "'Bangers', cursive",
            fontSize: '0.85rem',
            letterSpacing: '0.12em',
            color: DANGER_RED,
          }}
        >
          STICKERS
        </span>
        <span
          style={{
            fontFamily: 'monospace',
            fontSize: '0.55rem',
            color: 'rgba(255,255,255,0.2)',
            marginLeft: 'auto',
          }}
        >
          {sortedStickers.length} available
        </span>
      </div>

      {/* Grid */}
      <div
        style={{
          padding: 8,
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 6,
          maxHeight: 240,
          overflowY: 'auto',
        }}
      >
        {sortedStickers.length === 0 && (
          <div
            style={{
              gridColumn: '1 / -1',
              padding: '24px 0',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 8,
              color: 'rgba(255,255,255,0.2)',
            }}
          >
            <Layers size={24} style={{ opacity: 0.3 }} />
            <span style={{ fontFamily: 'monospace', fontSize: '0.65rem', letterSpacing: '0.08em' }}>
              NO STICKERS YET
            </span>
          </div>
        )}

        {sortedStickers.map(sticker => (
          <StickerThumbnail
            key={sticker.id}
            sticker={sticker}
            onSelect={() => {
              onSelect(sticker.imageUrl);
              onClose();
            }}
          />
        ))}
      </div>
    </div>
  );
};

const StickerThumbnail: React.FC<{
  sticker: PresetStickersResponse;
  onSelect: () => void;
}> = ({ sticker, onSelect }) => {
  const [hovered, setHovered] = React.useState(false);

  return (
    <button
      onMouseDown={e => { e.preventDefault(); onSelect(); }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title={sticker.name}
      style={{
        aspectRatio: '1',
        background: hovered ? 'rgba(230,57,70,0.12)' : 'rgba(255,255,255,0.04)',
        border: hovered
          ? `1px solid rgba(230,57,70,0.45)`
          : `1px solid rgba(255,255,255,0.08)`,
        borderRadius: 6,
        cursor: 'pointer',
        padding: 4,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        transition: 'all 0.12s ease',
        transform: hovered ? 'scale(1.08)' : 'scale(1)',
      }}
    >
      <img
        src={sticker.imageUrl}
        alt={sticker.name}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          display: 'block',
        }}
        loading="lazy"
      />
    </button>
  );
};

export default StickerPicker;
